/**
 * Tempo changes of the Song & Timing workspace (W2-08 timing; DESIGN.md §4.15 "TEMPO CHANGES", §5.2 "Tempo
 * changes"):
 *  - one row per beat with a tempo change, a meter change or both: beat, BPM and beats per bar, edited inline
 *    (values a row does not set are inherited and shown in italics; typing one adds it at the row's beat);
 *  - the beat-0 row is pinned (its beat cannot change, it cannot be deleted; a lock says why);
 *  - Add at playhead (Shift+B), Meter change, Insert beats and Remove range (`Core.transform`, one undoable edit);
 *  - the edit policy (notes keep their beats / keep their time), remembered per project;
 *  - the notice that other difficulties of the song still use the old tempo map.
 */

import { useCallback, useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { formatBeat } from '../../core/time/beatSyntax';
import { Beats } from '../../core/time/beat';
import type { Beat, BeatParseContext } from '../../core/time/types';
import type { EditorStore } from '../../state/types';
import { Button, Icon, IconButton, Popover, Segmented, Tooltip } from '../../ui';
import { InlineBeat, InlineText } from './InlineInputs';
import { m } from './timing.messages';
import { addMeterAtPlayhead, addTempoAtPlayhead } from './timingActions';
import { playheadBeat, type TimingView } from './timingDoc';
import { addTempoAt, deleteRow, insertBeats, moveRow, removeRange, setMeterAt, setPointBpm } from './timingEdits';
import { useEditorSelect, useSettingsValue, type TimingText } from './timingHooks';
import { formatBpm, parseBeatsPerBar, parseBpm, tempoRows, type TempoRow } from './timingModel';
import { otherDifficultiesNotice, setPolicy, useTimingSession, type TimingSessionState } from './timingSession';
import s from './Timing.module.css';

export interface TempoTableProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
}

const selectPolicy = (st: EditorStore) => st.tempoPolicy;

function numberText(v: number): string {
  return String(Math.round(v * 10000) / 10000);
}

export function TempoTable(props: TempoTableProps) {
  const { services, text, view } = props;
  const settings = useSettingsValue(services);
  const policy = useEditorSelect(services, selectPolicy);
  const project = services.store?.projectId ?? null;
  const selectNotice = useCallback((st: TimingSessionState) => otherDifficultiesNotice(st, project), [project]);
  const others = useTimingSession(services, selectNotice);
  const [invalid, setInvalid] = useState<string | null>(null);
  const rows = tempoRows(view.points, view.meter);
  const style = settings.editing.beatDisplay === 'plus' ? 'plus' : 'colon';
  const ctx = (current: Beat): BeatParseContext => ({
    snapDiv: services.editor.getState().snap.div,
    syntax: settings.editing.beatSyntax,
    tempo: view.tempo,
    current,
  });

  const report = (key: string) => (bad: boolean) => {
    setInvalid(bad ? key : null);
  };

  return (
    <div className={s.stack}>
      <table className={s.table} data-testid="timing-tempo-table">
        <thead>
          <tr>
            <th scope="col">{text.t(m.colBeat)}</th>
            <th scope="col">{text.t(m.colBpm)}</th>
            <th scope="col">{text.t(m.colMeter)}</th>
            <th scope="col" className={s.actionsCell}>
              <span className={s.subtle}>{text.t(m.colActions)}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <TableRow
              key={Beats.key(row.t)}
              row={row}
              services={services}
              text={text}
              style={style}
              ctx={ctx(row.t)}
              onInvalid={report}
            />
          ))}
        </tbody>
      </table>
      {invalid !== null && (
        <p className={s.error} role="alert">
          <Icon name="CircleAlert" size={14} />
          {text.t(invalid)}
        </p>
      )}
      <div className={s.row}>
        <Tooltip content={text.t(m.addAtPlayheadTip)} command="timing.addTempoAtPlayhead">
          <Button
            size="sm"
            icon="Plus"
            command="timing.addTempoAtPlayhead"
            onClick={() => {
              addTempoAtPlayhead(services);
            }}
          >
            {text.t(m.addAtPlayhead)}
          </Button>
        </Tooltip>
        <Tooltip content={text.t(m.addMeterTip)}>
          <Button
            size="sm"
            icon="ListPlus"
            onClick={() => {
              addMeterAtPlayhead(services);
            }}
          >
            {text.t(m.addMeter)}
          </Button>
        </Tooltip>
        <InsertBeatsForm services={services} text={text} view={view} ctx={ctx} style={style} />
        <RemoveRangeForm services={services} text={text} view={view} ctx={ctx} style={style} />
      </div>
      <Segmented<'keepBeats' | 'keepTime'>
        label={text.t(m.policyLabel)}
        value={policy}
        size="sm"
        options={[
          { value: 'keepBeats', label: text.t(m.policyKeepBeatsShort) },
          { value: 'keepTime', label: text.t(m.policyKeepTimeShort) },
        ]}
        onChange={(v) => {
          setPolicy(services, v);
        }}
      />
      <p className={s.help}>{text.t(policy === 'keepBeats' ? m.policyKeepBeatsHelp : m.policyKeepTimeHelp)}</p>
      {others > 0 && (
        <div className={s.notice} data-kind="warning" role="status" data-testid="timing-other-difficulties">
          <Icon name="TriangleAlert" size={16} />
          <span>{text.t(m.otherDifficulties, { count: others })}</span>
        </div>
      )}
      <p className={s.help}>{text.t(m.meterBefore)}</p>
    </div>
  );
}

interface TableRowProps {
  readonly row: TempoRow;
  readonly services: AppServices;
  readonly text: TimingText;
  readonly style: 'colon' | 'plus';
  readonly ctx: BeatParseContext;
  onInvalid(key: string): (bad: boolean) => void;
}

function TableRow(props: TableRowProps) {
  const { row, services, text, style, ctx } = props;
  const beat = formatBeat(row.t, style);
  return (
    <tr data-pinned={row.pinned} data-testid={`timing-row-${Beats.key(row.t)}`}>
      <td>
        {row.pinned ? (
          <Tooltip content={text.t(m.rowPinned)}>
            <span className={s.rowTight} tabIndex={0}>
              <Icon name="Lock" size={12} label={{ key: m.rowPinned }} />
              <span className={s.mono}>{beat}</span>
            </span>
          </Tooltip>
        ) : (
          <InlineBeat
            value={beat}
            ariaLabel={text.t(m.beatRowLabel)}
            ctx={ctx}
            onInvalid={props.onInvalid(m.beatInvalid)}
            onCommit={(t) => {
              moveRow(services, row, t);
            }}
          />
        )}
      </td>
      <td>
        <InlineText<number>
          value={row.point === null ? '' : formatBpm(row.bpm)}
          placeholder={row.point === null ? formatBpm(row.bpm) : undefined}
          ariaLabel={row.point === null ? text.t(m.bpmRowInheritedLabel, { beat }) : text.t(m.bpmRowLabel, { beat })}
          parse={parseBpm}
          onInvalid={props.onInvalid(m.bpmInvalid)}
          testId={`timing-row-bpm-${Beats.key(row.t)}`}
          onCommit={(bpm) => {
            if (row.point !== null) setPointBpm(services, row.point.id, bpm);
            else addTempoAt(services, row.t, bpm);
          }}
        />
      </td>
      <td>
        <InlineText<number>
          value={row.mark === null ? '' : numberText(row.beatsPerBar)}
          placeholder={row.mark === null ? numberText(row.beatsPerBar) : undefined}
          ariaLabel={row.mark === null ? text.t(m.meterInheritedLabel, { beat }) : text.t(m.meterLabel, { beat })}
          parse={parseBeatsPerBar}
          onInvalid={props.onInvalid(m.meterInvalid)}
          testId={`timing-row-meter-${Beats.key(row.t)}`}
          width="4em"
          onCommit={(v) => {
            setMeterAt(services, row.t, v);
          }}
        />
      </td>
      <td className={s.actionsCell}>
        {!row.pinned && (
          <IconButton
            icon="Trash2"
            size="sm"
            label={{ key: m.deleteRowLabel, params: { beat } }}
            onClick={() => {
              deleteRow(services, row);
            }}
          />
        )}
      </td>
    </tr>
  );
}

interface FormProps {
  readonly services: AppServices;
  readonly text: TimingText;
  readonly view: TimingView;
  readonly style: 'colon' | 'plus';
  ctx(current: Beat): BeatParseContext;
}

function InsertBeatsForm(props: FormProps) {
  const { services, text, view, style } = props;
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<Beat>(Beats.ZERO);
  const [length, setLength] = useState<Beat>(Beats.fromInt(4));
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) {
          const p = playheadBeat(services);
          setAt(p);
          setLength(Beats.fromF64(view.meterMap.beatsPerBarAt(p), 1920));
        }
        setOpen(o);
      }}
      label={text.t(m.insertBeats)}
      trigger={
        <Button size="sm" icon="BetweenHorizontalStart">
          {text.t(m.insertBeats)}
        </Button>
      }
    >
      <form
        className={s.popoverForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (insertBeats(services, at, length)) setOpen(false);
        }}
      >
        <label className={s.field}>
          <span className={s.label}>{text.t(m.insertAt)}</span>
          <InlineBeat
            value={formatBeat(at, style)}
            ariaLabel={text.t(m.insertAt)}
            ctx={props.ctx(at)}
            onCommit={setAt}
            autoFocus
          />
        </label>
        <label className={s.field}>
          <span className={s.label}>{text.t(m.insertLength)}</span>
          <InlineBeat
            value={formatBeat(length, 'plus')}
            ariaLabel={text.t(m.insertLength)}
            ctx={{ snapDiv: props.ctx(length).snapDiv, syntax: props.ctx(length).syntax, current: length }}
            onCommit={(b) => {
              if (Beats.cmp(b, Beats.ZERO) > 0) setLength(b);
            }}
          />
        </label>
        <Button type="submit" variant="primary" icon="BetweenHorizontalStart">
          {text.t(m.insertGo)}
        </Button>
      </form>
    </Popover>
  );
}

function RemoveRangeForm(props: FormProps) {
  const { services, text, view, style } = props;
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<Beat>(Beats.ZERO);
  const [to, setTo] = useState<Beat>(Beats.fromInt(4));
  const bad = !Beats.lt(from, to);
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        if (o) {
          const p = playheadBeat(services);
          setFrom(p);
          setTo(view.meterMap.nextBarStart(p));
        }
        setOpen(o);
      }}
      label={text.t(m.removeRange)}
      trigger={
        <Button size="sm" icon="BetweenHorizontalEnd">
          {text.t(m.removeRange)}
        </Button>
      }
    >
      <form
        className={s.popoverForm}
        onSubmit={(e) => {
          e.preventDefault();
          if (removeRange(services, from, to)) setOpen(false);
        }}
      >
        <label className={s.field}>
          <span className={s.label}>{text.t(m.removeFrom)}</span>
          <InlineBeat
            value={formatBeat(from, style)}
            ariaLabel={text.t(m.removeFrom)}
            ctx={props.ctx(from)}
            onCommit={setFrom}
            autoFocus
          />
        </label>
        <label className={s.field}>
          <span className={s.label}>{text.t(m.removeTo)}</span>
          <InlineBeat
            value={formatBeat(to, style)}
            ariaLabel={text.t(m.removeTo)}
            ctx={props.ctx(to)}
            onCommit={setTo}
          />
        </label>
        {bad && (
          <p className={s.error} role="alert">
            {text.t(m.rangeInvalid)}
          </p>
        )}
        <Button
          type="submit"
          variant="danger"
          icon="BetweenHorizontalEnd"
          disabledReason={bad ? { key: m.rangeInvalid } : null}
        >
          {text.t(m.removeGo)}
        </Button>
      </form>
    </Popover>
  );
}
